import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { usePDVContext } from '../contexts/PDVContext';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Package, 
  DollarSign, 
  Users,
  LogOut,
  Receipt,
  ShoppingBag,
  Settings,
  Briefcase,
  ClipboardList,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  KeyRound,
  Wallet
} from 'lucide-react';
import { SwitchOperatorModal } from './SwitchOperatorModal';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const company = useCompanySettings();
  const { requestNavigation } = usePDVContext();
  const [showSwitch, setShowSwitch] = useState(false);

  // Recolhe automaticamente quando entra no PDV (modo "kiosk").
  // Em outras telas, mantém o estado escolhido pelo usuário (localStorage).
  const isPdv = location.pathname.startsWith('/pdv');
  const [collapsed, setCollapsed] = useState(() => {
    if (location.pathname.startsWith('/pdv')) return true;
    try { return localStorage.getItem('sf_sidebar_collapsed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    // Ao navegar para PDV, recolhe automaticamente
    if (isPdv) setCollapsed(true);
  }, [isPdv]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { if (!isPdv) localStorage.setItem('sf_sidebar_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const handleNav = (href) => (e) => {
    e.preventDefault();
    requestNavigation(() => navigate(href));
  };

  const handleLogout = () => {
    requestNavigation(async () => {
      await logout();
      navigate('/login');
    });
  };

  const allNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, perm: 'dashboard.view' },
    { name: 'PDV', href: '/pdv', icon: ShoppingCart, perm: 'pdv.access' },
    { name: 'Vendas', href: '/sales', icon: Receipt, perm: 'sales.view' },
    { name: 'Clientes à Vista', href: '/cash-customers', icon: Wallet, perm: 'customers.manage' },
    { name: 'Produtos', href: '/products', icon: Package, perm: 'products.view' },
    { name: 'Clientes', href: '/customers', icon: Users, perm: 'customers.manage' },
    { name: 'Gestão Financeira', href: '/purchase-notes', icon: ShoppingBag, perm: 'purchase_notes.manage' },
    { name: 'Fluxo de Caixa', href: '/cashflow', icon: DollarSign, perm: 'cashflow.view' },
    { name: 'Funcionários', href: '/employees', icon: Briefcase, perm: 'employees.manage' },
    { name: 'Usuários', href: '/users', icon: Users, perm: 'users.manage' },
    { name: 'Liberar Sistema', href: '/billing', icon: CreditCard, adminOnly: true },
    { name: 'Licença Local', href: '/license', icon: KeyRound, adminOnly: true },
    { name: 'Configurações', href: '/settings', icon: Settings, perm: 'dashboard.view' }
  ];

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const navigation = allNavigation.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    return hasPermission(user, item.perm);
  });

  // === MODO RECOLHIDO: apenas seta para expandir ===
  if (collapsed) {
    return (
      <div className="sf-sidebar flex flex-col h-screen w-12 border-r items-center py-3 gap-2">
        {company.logo_base64 && (
          <img src={company.logo_base64} alt={company.trade_name || 'Logo'} className="h-8 w-8 rounded-full object-cover shrink-0" data-testid="sidebar-company-logo-collapsed" />
        )}
        <button
          onClick={toggleCollapsed}
          data-testid="sidebar-expand-btn"
          title="Expandir menu"
          className="w-9 h-9 rounded-md bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
        >
          <ChevronRight size={20} />
        </button>
        <div className="w-full h-px bg-slate-500/30 my-1" />
        {/* Atalhos visuais (somente ícones, clicáveis) */}
        {navigation.slice(0, 5).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={handleNav(item.href)}
              title={item.name}
              data-testid={`nav-collapsed-${item.name.toLowerCase().replace(/\s/g, '-').replace(/[ãáéíóúç]/g, c => ({ã:'a',á:'a',é:'e',í:'i',ó:'o',ú:'u',ç:'c'}[c] || c))}`}
              className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${
                isActive ? 'sf-sidebar-item-active' : 'sf-sidebar-item'
              }`}
            >
              <Icon size={18} />
            </Link>
          );
        })}
        <div className="flex-1" />
        <button
          onClick={handleLogout}
          data-testid="logout-collapsed-button"
          title="Sair"
          className="w-9 h-9 rounded-md text-red-400 hover:bg-red-500/20 flex items-center justify-center transition-colors"
        >
          <LogOut size={18} />
        </button>
      </div>
    );
  }

  // === MODO EXPANDIDO (padrão) ===
  return (
    <div className="sf-sidebar flex flex-col h-screen w-64 border-r">
      <div className="p-6 border-b sf-sidebar relative" style={{ borderColor: 'var(--sf-sidebar-border)' }}>
        <button
          onClick={toggleCollapsed}
          data-testid="sidebar-collapse-btn"
          title="Recolher menu"
          className="absolute top-3 right-3 w-7 h-7 rounded-md bg-slate-500/20 hover:bg-slate-500/40 sf-sidebar-text flex items-center justify-center transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-black sf-sidebar-title tracking-tight">Smart Flow</h1>
            {!company.logo_base64 && company.trade_name && company.trade_name !== 'Smart Flow' && (
              <p className="text-sm font-medium text-blue-600 mt-1">{company.trade_name}</p>
            )}
            {company.city && (
              <p className="text-xs uppercase tracking-[0.2em] sf-sidebar-text mt-1 opacity-75">
                {company.city}{company.state ? ` - ${company.state}` : ''}
              </p>
            )}
          </div>
          {company.logo_base64 && (
            <>
              <span className="w-px h-9 bg-slate-400/40 shrink-0" aria-hidden="true" />
              <img src={company.logo_base64} alt={company.trade_name || 'Logo da loja'} className="h-[52px] w-[52px] rounded-full object-cover shrink-0" data-testid="sidebar-company-logo" />
            </>
          )}
        </div>
      </div>

      <nav className="flex-1 min-h-0 p-3 overflow-hidden">
        <div
          className="grid grid-cols-2 gap-2 h-full"
          style={{ gridTemplateRows: `repeat(${Math.ceil((navigation.length + 3) / 2)}, minmax(0, 1fr))` }}
          data-testid="sidebar-nav-grid"
        >
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={handleNav(item.href)}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-').replace(/[ãáéíóúç]/g, c => ({ã:'a',á:'a',é:'e',í:'i',ó:'o',ú:'u',ç:'c'}[c] || c))}`}
                style={{ borderColor: 'var(--sf-sidebar-border)' }}
                className={`min-h-0 rounded-lg border flex flex-col items-center justify-center gap-1 text-center px-1.5 py-1 transition-colors duration-200 ${
                  isActive ? 'sf-sidebar-item-active' : 'sf-sidebar-item'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium text-[11px] leading-tight">{item.name}</span>
              </Link>
            );
          })}

          {/* Quadro do usuário logado */}
          <div
            style={{ borderColor: 'var(--sf-sidebar-border)' }}
            className="min-h-0 rounded-lg border flex flex-col items-center justify-center gap-1 text-center px-1.5 py-1 sf-sidebar-item"
            data-testid="sidebar-user-tile"
          >
            <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <span className="font-medium text-[11px] leading-tight truncate max-w-full sf-sidebar-title">{user?.name}</span>
            <span className="text-[9px] uppercase sf-sidebar-text leading-none">{user?.role}</span>
          </div>

          {/* Trocar Usuário */}
          <button
            onClick={() => setShowSwitch(true)}
            data-testid="sidebar-switch-user-btn"
            style={{ borderColor: 'var(--sf-sidebar-border)' }}
            className="min-h-0 rounded-lg border flex flex-col items-center justify-center gap-1 text-center px-1.5 py-1 text-amber-500 hover:bg-amber-500/10 transition-colors duration-200"
          >
            <UserPlus size={20} />
            <span className="font-medium text-[11px] leading-tight">Trocar Usuário</span>
          </button>

          {/* Sair */}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            style={{ borderColor: 'var(--sf-sidebar-border)' }}
            className="min-h-0 rounded-lg border flex flex-col items-center justify-center gap-1 text-center px-1.5 py-1 text-red-500 hover:bg-red-500/10 transition-colors duration-200"
          >
            <LogOut size={20} />
            <span className="font-medium text-[11px] leading-tight">Sair</span>
          </button>
        </div>
      </nav>

      <SwitchOperatorModal
        open={showSwitch}
        onClose={() => setShowSwitch(false)}
        currentUser={user}
      />
    </div>
  );
};
